/**
 * useSidebarMenu 훅에서 추출된 순수 유틸리티 함수 모음
 */
import type { Feature, SDKType } from '../../types/global.types';

import { getLogger } from '@/lib/logger/logger';

import { getMenuById } from '../../config/menu.config';
import { SIDEBAR_LAYOUT } from '../constants/layout';

const logger = getLogger('sidebarMenuUtils');

/** ---------- 타입 가드 ---------- */
type RawFeature = Partial<Feature>;

export const isValidFeature = (f: RawFeature): f is Feature => {
  return (
    typeof f?.id === 'string' &&
    typeof f?.title === 'string' &&
    (typeof f?.category === 'string' || typeof f?.category === 'undefined')
  );
};

export const isValidSDKCategory = (value: string): value is SDKType => {
  const validCategories = ['hwp', 'hcell', 'docdiff', 'ocr', 'dataloader'];
  return validCategories.includes(value);
};

/** ---------- Feature 정규화 ---------- */
export const normalizeFeature = (f: Feature, defaultCategory: string): Feature => ({
  id: f.id,
  title: f.title,
  description: f.description ?? '',
  category: f.category ?? defaultCategory,
  testData: f.testData ?? {},
});

export const populateFeaturesMap = (features: Feature[], map: { [key: string]: Feature }): void => {
  for (const f of features) {
    map[f.id] = f;
  }
};

/** ---------- Feature 로더 ---------- */
export const loadCategoryFeatures = async (
  category: { id: string },
  language: string,
): Promise<{ id: string; features: Feature[] }> => {
  let categoryFeatures: Feature[] = [];

  const menuConfig = getMenuById(category.id);
  if (menuConfig?.hasFeatures && menuConfig.featuresLoader) {
    logger.info(`Loading features for ${category.id} (lang: ${language})`);
    const raw = (await menuConfig.featuresLoader(language)) as Array<RawFeature>;

    const valid = raw.filter(isValidFeature);
    categoryFeatures = valid.map(f => normalizeFeature(f, category.id));
  }

  return { id: category.id, features: categoryFeatures };
};

/** ---------- 높이 계산 ---------- */
export const calculateFeatureListHeight = (
  expandedCategories: { [key: string]: boolean },
  sidebarHeight: number,
): number => {
  const availableHeight = sidebarHeight - SIDEBAR_LAYOUT.HEADER_HEIGHT;

  const collapsedCount = Object.values(expandedCategories).filter(v => !v).length;
  const collapsedHeight = collapsedCount * SIDEBAR_LAYOUT.COLLAPSED_CATEGORY_HEIGHT;

  const expandedCount = Object.values(expandedCategories).filter(v => v).length || 1;
  const totalExpandedHeight =
    availableHeight - collapsedHeight - expandedCount * SIDEBAR_LAYOUT.CATEGORY_HEADER_HEIGHT;

  return Math.max(
    SIDEBAR_LAYOUT.MIN_FEATURE_LIST_HEIGHT,
    Math.floor(totalExpandedHeight / expandedCount),
  );
};
